import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import MultiSelect from '../components/ContactCenter/MultiSelect';
import ArcGauge, { fillTowards, fillUnder } from '../components/ContactCenter/ArcGauge';
import VpPodTrends from '../components/ContactCenter/VpPodTrends';
import usePersisted from '../components/ContactCenter/usePersisted';
import { podTone } from '../components/ContactCenter/badges';
import { fmtAsOf, oldestAsOf, fmtCount, fmtClock, fmtMoney, fmtPct, STATUS } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · VP Dashboard — the old app's /vp page.
//
// "Pod roll-up · trends · comparison · exceptions". Seven stacked sections
// rather than tabs, in the order the old page's own export picker lists them,
// so this is its structure rather than a reading of a screenshot:
//
//   Gauges / KPIs · Trends over time · Pod ranking · Cost per call & spend ·
//   Period-over-period · SLA exceptions · City insights
//
// The second of the two pages migration 053 folded into `leadership`; 080
// splits the views back out. Like the Exec page, every figure is the Board's
// own, re-shaped server-side, so a VP and a director cannot read two different
// service levels for the same week.

// The old page's range control is a dropdown of four presets with Custom as a
// separate button beside it, not a fifth entry in the list — 7d / 14d / 30d /
// QTD were taken out of it in its own layout sprint and are not coming back.
const RANGES = [['today', 'Today'], ['yesterday', 'Yesterday'], ['mtd', 'MTD'], ['ytd', 'YTD']];
const RANGE_LABEL = Object.fromEntries(RANGES);
// Voice, Callback, VIP — the old page's order, which is not the order the
// other pages use and is what a reader comparing the two will notice.
const CHANNELS = ['Voice', 'Callback', 'VIP', 'Email', 'Chat', 'Voicemail'];
const RANK_BY = [['serviceLevel', 'Service level'], ['handled', 'Volume'], ['aht', 'AHT'], ['abandonPct', 'Abandon %']];

const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const addDays = (ymd, n) => new Date(`${ymd}T12:00:00Z`).getTime() + n * 86400000;
const shift = (ymd, n) => new Date(addDays(ymd, n)).toISOString().slice(0, 10);

// Each preset as an explicit pair of dates: the server takes a start and an
// end and clamps to the data floor, so the page never has to name a window
// the server would have to reinterpret.
function windowFor(mode, today, custom) {
  if (mode === 'today') return { start: today, end: today };
  if (mode === 'yesterday') return { start: shift(today, -1), end: shift(today, -1) };
  if (mode === 'mtd') return { start: `${today.slice(0, 8)}01`, end: today };
  if (mode === 'ytd') return { start: `${today.slice(0, 4)}-01-01`, end: today };
  return custom.start && custom.end ? { ...custom } : null;
}

// Spend runs to five and six figures, where the shared `fmtMoney` — right for
// a $2.33 cost per call — prints $34672.00 and has to be counted by eye. The
// separators are only ever wanted on this one column, so it is local rather
// than a change to the format every other page shares.
const fmtSpend = (n) => (n == null ? '—'
  : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const arcTone = (status) => ({ good: 'good', watch: 'amber', bad: 'bad' }[status] || null);
const podChip = (pod) => (
  <span style={{ ...s.podBadge, background: podTone(pod).bg, color: podTone(pod).fg }}>
    <span style={{ ...s.podDot, background: podTone(pod).dot }} />{pod}
  </span>
);

// A period-over-period cell. Direction decides the colour, not the sign: AHT
// falling is good and volume falling is neither, so each column says which way
// it reads rather than every arrow being green when it points up.
function Delta({ value, fmt, betterDir }) {
  if (value == null) return <span style={{ color: c.textFaint }}>—</span>;
  if (value === 0) return <span style={{ color: c.textMuted }}>no change</span>;
  const up = value > 0;
  const good = betterDir === 'neutral' ? null : (betterDir === 'down' ? !up : up);
  return (
    <span style={{ color: good === null ? c.textMuted : good ? c.successText : c.dangerText, fontWeight: 600 }}>
      {up ? '▲' : '▼'} {fmt(Math.abs(value))}
    </span>
  );
}

export default function ContactCenterVp() {
  const today = pacificToday();
  const [mode, setMode] = usePersisted('vp.range', 'today');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [pods, setPods] = usePersisted('vp.selectedPods', []);
  const [channels, setChannels] = usePersisted('vp.selectedChannels', ['Voice', 'VIP', 'Callback']);
  const [rankBy, setRankBy] = usePersisted('vp.rankBy', 'serviceLevel');

  const range = windowFor(mode, today, custom);
  const rangeLabel = RANGE_LABEL[mode] || 'Custom';
  // Hours are a trend only on the two single-day presets. A custom range of
  // one day stays on days, as it does on the old page — the server would
  // serve the hours either way, so this is the page's decision to match.
  const byHour = mode === 'today' || mode === 'yesterday';

  const params = {
    ...range,
    ...(pods.length ? { pods: pods.join(',') } : {}),
    ...(channels.length ? { channels: channels.join(',') } : {}),
  };

  const q = useQuery({
    queryKey: ['cc-vp', range?.start, range?.end, pods.join(','), channels.join(',')],
    queryFn: () => api.get('/contact-center/vp', { params }),
    enabled: !!range,
    refetchInterval: 5 * 60 * 1000,
  });
  const d = q.data;
  const k = d?.kpis;

  // Ranking is re-sorted in the browser: the server ships one ordered list and
  // changing the column is a reorder of rows already on screen, not a round
  // trip. Pods with no figure stay last whichever column is chosen — "no data"
  // is not a ranking position.
  const ranked = !d ? [] : [...d.ranking].sort((a, b) => {
    const [x, y] = [a[rankBy], b[rankBy]];
    if (x == null && y == null) return a.pod.localeCompare(b.pod);
    if (x == null) return 1;
    if (y == null) return -1;
    return (rankBy === 'aht' || rankBy === 'abandonPct') ? x - y : y - x;
  });
  const rankMax = Math.max(...ranked.map((p) => Math.abs(p[rankBy] ?? 0)), 1);
  const rankFmt = { serviceLevel: fmtPct, handled: fmtCount, aht: fmtClock, abandonPct: fmtPct }[rankBy];

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title="VP Dashboard"
        subtitle={d ? `${d.range.label} · Pod roll-up · trends · comparison · exceptions`
          : 'Pod roll-up · trends · comparison · exceptions'}
        meta={fmtAsOf(oldestAsOf(d?.asOf)) ? <span style={s.chip}>Data as of {fmtAsOf(oldestAsOf(d.asOf))}</span> : null}
        actions={(
          <div style={s.row}>
            <MultiSelect label="Scope" options={d?.allPods || []} selected={pods} onChange={setPods}
              allLabel="All" noun="pods" emptyMeansAll />
            <MultiSelect label="Channel" options={CHANNELS} selected={channels} onChange={setChannels}
              allLabel="All" noun="channels" emptyMeansAll />
            <label style={s.rangeWrap}>
              <span style={s.rangeLabel}>Range:</span>
              <select id="cc-vp-range" value={mode === 'custom' ? 'mtd' : mode}
                onChange={(e) => setMode(e.target.value)} aria-label="Range" style={s.select}>
                {RANGES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </label>
            <button type="button" onClick={() => setMode('custom')} aria-pressed={mode === 'custom'}
              style={mode === 'custom' ? s.pillOn : s.pillBtn}>Custom</button>
            {mode === 'custom' && (
              <>
                <input id="cc-vp-start" type="date" aria-label="Start date" max={today}
                  value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                <input id="cc-vp-end" type="date" aria-label="End date" max={today}
                  value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
              </>
            )}
          </div>
        )}
      />

      {mode === 'custom' && !range && (
        <section style={s.empty} aria-label="Select a date range">
          <div style={s.emptyTitle}>Select a date range</div>
          <div style={s.emptySub}>Pick a start and end date above to load custom data.</div>
        </section>
      )}
      {q.isLoading && range && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the VP Dashboard: {q.error.message}</div>}
      {d?.range.clamped && (
        <div style={s.notice}>Contact Center data starts on June 4, 2026, when Athens moved to Genesys; the window starts there.</div>
      )}

      {d && (
        <>
          {/* ── 1. Gauges / KPIs ─────────────────────────────────────────
              Six arcs in one row, in the old page's order: Handled first,
              because it is the question the rest qualify. Each carries the
              channels it was actually measured over, which differ per metric
              and are the thing a reader otherwise assumes. */}
          <SectionHeading title="Gauges / KPIs" subtitle={`${rangeLabel} · ${pods.length ? `${pods.length} pods` : 'All pods'}`} />
          <div style={s.gauges}>
            <ArcGauge title={`Handled · ${rangeLabel}`} value={k.handled.value} display={fmtCount(k.handled.value)}
              sublabel={channelLabel(d.channels)}
              tone={k.handled.forecast?.vsPct == null ? null : k.handled.forecast.vsPct >= -10 ? 'good' : 'bad'}
              fill={fillTowards(k.handled.value, k.handled.forecast?.forecast)}
              goalFraction={k.handled.forecast?.forecast > 0 ? 1 : null}
              goalText={k.handled.forecast?.forecast > 0 ? `Forecast ${fmtCount(k.handled.forecast.forecast)}` : 'No forecast'}
              beat={k.handled.forecast?.vsPct ?? null}
              footnote={k.handled.forecast?.forecast > 0 ? null : `no forecast for ${rangeLabel}`} />

            <ArcGauge title={`Service Level · ${rangeLabel}`} value={k.serviceLevel.value}
              display={fmtPct(k.serviceLevel.value)} sublabel="Voice + VIP"
              tone={arcTone(STATUS.serviceLevel(k.serviceLevel.value))}
              fill={fillTowards(k.serviceLevel.value, d.slTarget)}
              goalFraction={d.slTarget / 100} goalLabel={d.slTarget} goalText={`Goal ${d.slTarget}%`}
              beat={pctOfGoal(k.serviceLevel.value, d.slTarget, 'up')} />

            {/* Speed of answer is the one gauge whose scope is wider than the
                others: the organization's figure folds Callback in, as the old
                app's does, so the label has to say so or it reads as a typo
                beside its neighbours. */}
            <ArcGauge title={`ASA · ${rangeLabel}`} value={k.asa.value} display={fmtClock(k.asa.value)}
              sublabel="Voice + VIP + Callback" tone={arcTone(STATUS.asa(k.asa.value))}
              fill={fillUnder(k.asa.value, d.targets.asaSecs)} goalFraction={1}
              goalText={`Goal ${fmtClock(d.targets.asaSecs)}`}
              beat={pctOfGoal(k.asa.value, d.targets.asaSecs, 'down')} />

            <ArcGauge title={`AHT · ${rangeLabel}`} value={k.aht.value} display={fmtClock(k.aht.value)}
              sublabel="Voice + VIP" tone={arcTone(STATUS.aht(k.aht.value))}
              fill={fillUnder(k.aht.value, d.targets.ahtSecs)} goalFraction={1}
              goalText={`Goal ${fmtClock(d.targets.ahtSecs)}`}
              beat={pctOfGoal(k.aht.value, d.targets.ahtSecs, 'down')} />

            <ArcGauge title={`Abandon % · ${rangeLabel}`} value={k.abandonPct.value}
              display={fmtPct(k.abandonPct.value)} sublabel="Voice + VIP + Callback"
              tone={arcTone(STATUS.abandonPct(k.abandonPct.value))}
              fill={fillUnder(k.abandonPct.value, d.targets.abandonPct)} goalFraction={1}
              goalText={`Goal ${d.targets.abandonPct}%`}
              beat={pctOfGoal(k.abandonPct.value, d.targets.abandonPct, 'down')} />

            <ArcGauge title={`Transfer % · ${rangeLabel}`} value={d.org.transferPct}
              display={fmtPct(d.org.transferPct)} sublabel="distinct conversations"
              tone={d.org.transferPct == null ? null : d.org.transferPct <= d.targets.transferPct ? 'good' : 'bad'}
              fill={fillUnder(d.org.transferPct, d.targets.transferPct)} goalFraction={1}
              goalText={`Goal ${d.targets.transferPct}%`}
              beat={pctOfGoal(d.org.transferPct, d.targets.transferPct, 'down')}
              footnote={d.org.transferred == null ? null : `${fmtCount(d.org.transferred)} transfers`} />
          </div>

          {/* ── 2. Trends over time ──────────────────────────────────────── */}
          {/* Four charts, one series per pod, at the old page's own grain: a
              day per point, or an hour on Today and Yesterday. This replaced
              three monthly sparklines that the Board already draws — the old
              VP page has no monthly view, and two pages showing the same three
              months is a duplicate rather than a second opinion. */}
          <SectionHeading title="Trends over time"
            subtitle={`${rangeLabel} · ${byHour ? 'by hour' : 'by day'} · ${d.range.label}`} />
          <VpPodTrends podDaily={d.podDaily} podHourly={d.podHourly} slTarget={d.slTarget} hourly={byHour} />

          {/* ── 3. Pod ranking ───────────────────────────────────────────── */}
          <SectionHeading title="Pod ranking" subtitle={rangeLabel} />
          <section style={s.card} aria-label="Pod ranking">
            <div style={s.head}>
              <span style={s.seg} role="group" aria-label="Rank by">
                {RANK_BY.map(([id, label]) => (
                  <button key={id} type="button" aria-pressed={rankBy === id} onClick={() => setRankBy(id)}
                    style={rankBy === id ? s.segOn : s.segOff}>{label}</button>
                ))}
              </span>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.thNarrow}>#</th>
                  <th style={s.th}>Pod</th>
                  <th style={s.thNum}>{RANK_BY.find(([id]) => id === rankBy)[1]}</th>
                  <th style={s.th} aria-label="Relative" />
                  <th style={s.thNum}>Handled</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((p, i) => (
                  <tr key={p.pod}>
                    <td style={s.tdRank}>{i + 1}</td>
                    <td style={s.td}>{podChip(p.pod)}</td>
                    <td style={{ ...s.num, fontWeight: 600 }}>{rankFmt(p[rankBy])}</td>
                    <td style={s.tdBar}>
                      <span style={s.barTrack}>
                        <span style={{ ...s.barFill, width: `${Math.min(100, (Math.abs(p[rankBy] ?? 0) / rankMax) * 100)}%`,
                          background: podTone(p.pod).dot }} />
                      </span>
                    </td>
                    <td style={s.num}>{fmtCount(p.handled)}</td>
                  </tr>
                ))}
                {!ranked.length && <tr><td colSpan={5} style={s.noRows}>No pod figures for this window.</td></tr>}
              </tbody>
            </table>
            <p style={s.foot}>
              Best to worst on the column chosen; AHT and Abandon % rank lowest-first, because on those
              a smaller number is the better one. A pod with no figure for that column is listed last
              rather than ranked at zero.
            </p>
          </section>

          {/* ── 4. Cost per call & spend ─────────────────────────────────── */}
          <SectionHeading title="Cost per call & spend" subtitle={`${rangeLabel} · voice scope · modelled`} />
          <section style={s.card} aria-label="Cost per call and spend">
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Pod</th>
                  <th style={s.thNum}>Handled</th>
                  <th style={s.thNum}>Cost / call</th>
                  <th style={s.thNum}>Modelled spend</th>
                </tr>
              </thead>
              <tbody>
                {d.cost.map((p) => (
                  <tr key={p.pod}>
                    <td style={s.td}>{podChip(p.pod)}</td>
                    <td style={s.num}>{fmtCount(p.handled)}</td>
                    <td style={s.num}>{fmtMoney(p.costPerCall)}</td>
                    <td style={{ ...s.num, fontWeight: 600 }}>{fmtSpend(p.spend)}</td>
                  </tr>
                ))}
                {!d.cost.length && <tr><td colSpan={4} style={s.noRows}>No cost figures for this window.</td></tr>}
              </tbody>
            </table>
            <p style={s.foot}>
              Cost per call is modelled from AHT and the platform cost model — it is not a Finance actual,
              and spend is that model multiplied by the pod&rsquo;s handled calls. Use it to compare pods
              with each other, not to reconcile an invoice.
            </p>
          </section>

          {/* ── 5. Period-over-period ────────────────────────────────────── */}
          <SectionHeading title="Period-over-period"
            subtitle={`${rangeLabel} vs ${d.priorLabel || 'the preceding window'}`} />
          <section style={s.card} aria-label="Period over period">
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Pod</th>
                  <th style={s.thNum}>Service level</th>
                  <th style={s.thNum}>Δ</th>
                  <th style={s.thNum}>AHT</th>
                  <th style={s.thNum}>Δ</th>
                  <th style={s.thNum}>Handled</th>
                  <th style={s.thNum}>Δ</th>
                  <th style={s.thNum}>Abandon %</th>
                  <th style={s.thNum}>Δ</th>
                </tr>
              </thead>
              <tbody>
                {d.deltas.map((p) => (
                  <tr key={p.pod}>
                    <td style={s.td}>{podChip(p.pod)}</td>
                    <td style={s.num}>{fmtPct(p.serviceLevel)}</td>
                    <td style={s.num}><Delta value={p.serviceLevelDelta} fmt={(v) => `${v.toFixed(1)} pts`} betterDir="up" /></td>
                    <td style={s.num}>{fmtClock(p.aht)}</td>
                    <td style={s.num}><Delta value={p.ahtDelta} fmt={fmtClock} betterDir="down" /></td>
                    <td style={s.num}>{fmtCount(p.handled)}</td>
                    <td style={s.num}><Delta value={p.handledDelta} fmt={fmtCount} betterDir="neutral" /></td>
                    <td style={s.num}>{fmtPct(p.abandonPct)}</td>
                    <td style={s.num}><Delta value={p.abandonDelta} fmt={(v) => `${v.toFixed(1)} pts`} betterDir="down" /></td>
                  </tr>
                ))}
                {!d.deltas.length && <tr><td colSpan={9} style={s.noRows}>No pod figures for this window.</td></tr>}
              </tbody>
            </table>
            {d.deltas.some((p) => !p.hadPrior) && (
              <p style={s.foot}>
                A blank Δ is a pod with nothing in the prior window, not a pod that held steady.
              </p>
            )}
          </section>

          {/* ── 6. SLA exceptions ────────────────────────────────────────── */}
          <SectionHeading title="SLA exceptions" subtitle={`${rangeLabel} · pods below ${d.slTarget}%`} />
          <section style={s.card} aria-label="SLA exceptions">
            {d.exceptions.length ? (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Pod</th>
                    <th style={s.thNum}>Service level</th>
                    <th style={s.thNum}>Short of goal</th>
                    <th style={s.thNum}>Handled</th>
                  </tr>
                </thead>
                <tbody>
                  {d.exceptions.map((p) => (
                    <tr key={p.pod}>
                      <td style={s.td}>{podChip(p.pod)}</td>
                      <td style={{ ...s.num, color: c.dangerText, fontWeight: 600 }}>{fmtPct(p.serviceLevel)}</td>
                      <td style={s.num}>{p.shortfall.toFixed(1)} pts</td>
                      <td style={s.num}>{fmtCount(p.handled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={s.ok}>Every pod with a service level met the {d.slTarget}% goal this period.</p>
            )}
            {!!d.unjudged.length && (
              <p style={s.foot}>
                No service level for {d.unjudged.join(', ')} in this window, so {d.unjudged.length === 1 ? 'it is' : 'they are'} neither
                an exception nor clear — there were no queue calls to measure.
              </p>
            )}
          </section>

          {/* ── 7. City insights ─────────────────────────────────────────── */}
          <SectionHeading title="City insights" subtitle="Top cities by volume" />
          <section style={s.card} aria-label="City insights">
            {!d.city ? (
              <p style={s.muted}>City insights could not be built for this window.</p>
            ) : (
              <>
                <div style={s.kpis}>
                  <Kpi title="Conversations" value={fmtCount(d.city.summary.total)} sub="Overall volume" />
                  <Kpi title="Via CompanyId" value={fmtPct(d.city.summary.companyPct)} sub={fmtCount(d.city.summary.byCompany)} />
                  <Kpi title="Via zip lookup" value={fmtPct(d.city.summary.zipPct)} sub={fmtCount(d.city.summary.byZip)} />
                  <Kpi title="Unknown" value={fmtPct(d.city.summary.unknownPct)} sub={fmtCount(d.city.summary.unknown)} />
                </div>
                <h3 style={s.h3}>Pod breakdown <span style={s.sectionSub}>resolution quality across the recent window</span></h3>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Pod</th>
                      <th style={s.thNum}>Conversations</th>
                      <th style={s.thNum}>% of total</th>
                      <th style={s.th}>Top city</th>
                      <th style={s.th}>Top wrap-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.city.pods.map((p) => (
                      <tr key={p.pod}>
                        <td style={s.td}>{podChip(p.pod)}</td>
                        <td style={s.num}>{fmtCount(p.conversations)}</td>
                        <td style={s.num}>{fmtPct(p.pctOfTotal)}</td>
                        <td style={s.td}>
                          {p.topCity ?? <span style={{ color: c.textFaint }}>—</span>}
                          {p.topCityIsNotFirst && <span style={s.flag} title="Not the first city seen on the conversation"> ·</span>}
                        </td>
                        <td style={s.td}>{p.topWrapup ?? <span style={{ color: c.textFaint }}>—</span>}</td>
                      </tr>
                    ))}
                    {!d.city.pods.length && <tr><td colSpan={5} style={s.noRows}>No city figures for this window.</td></tr>}
                  </tbody>
                </table>
                <p style={s.foot}>
                  Source confidence: a city resolved from the caller&rsquo;s CompanyId is the account we hold;
                  one from a zip lookup is inferred from the number they called from and can be wrong for a
                  mobile. Unknown is neither. The Channel filter above scopes this section alone; the queue
                  figures higher up each carry their own fixed scope, named under the gauge, and do not
                  change with it.
                </p>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// How far past the goal, as a share of it — the arc's own delta line, matching
// the Director and Manager gauges rather than the Board's native units.
function pctOfGoal(value, target, betterDir) {
  if (value == null || !(target > 0)) return null;
  const raw = betterDir === 'down' ? target - value : value - target;
  return Math.round((raw / target) * 1000) / 10;
}

// The old page's section heading: a small uppercase title with a plain
// subtitle beside it, sitting above the content rather than inside a card.
function SectionHeading({ title, subtitle }) {
  return (
    <div style={s.sectionHead}>
      <h2 style={s.sectionTitle}>{title}</h2>
      {subtitle && <span style={s.sectionSub}>{subtitle}</span>}
    </div>
  );
}

// Which channels a figure was measured over, worded the way the old page
// words it: two or fewer are named, more are counted.
function channelLabel(list) {
  if (!list?.length) return 'All channels';
  if (list.length <= 2) return list.join(' + ');
  return `${list.length} channels`;
}

function Kpi({ title, value, sub, tone }) {
  return (
    <section style={s.kpi} aria-label={title}>
      <div style={s.kpiTitle}>{title}</div>
      <div style={{ ...s.kpiValue, color: tone ? ({ good: c.successText, amber: c.warningText, bad: c.dangerText }[tone]) : c.textStrong }}>
        {value}
      </div>
      <div style={s.kpiSub}>{sub}</div>
    </section>
  );
}

const s = {
  page:       { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:        { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  sectionHead: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', margin: '10px 0 -4px' },
  sectionTitle: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: c.textMuted, margin: 0 },
  sectionSub: { fontSize: '12px', fontWeight: 400, color: c.textFaint },
  gauges:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px' },
  rangeWrap:  { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  rangeLabel: { fontSize: '11px', fontWeight: 600, color: c.textMuted },
  select:     { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px',
    fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' },
  kpis:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' },
  kpi:        { ...card.base, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '2px' },
  kpiTitle:   { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted },
  kpiValue:   { fontSize: '24px', fontWeight: 650, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
  kpiSub:     { fontSize: '11px', color: c.textMuted, fontVariantNumeric: 'tabular-nums' },
  card:       { ...card.base, padding: '14px 18px 12px' },
  head:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' },
  h2:         { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 0 8px' },
  h3:         { fontSize: '12px', fontWeight: 650, color: c.text, margin: '14px 0 6px' },
  seg:        { display: 'inline-flex', gap: '2px', background: c.surfaceSunk, borderRadius: '8px', padding: '3px' },
  segOff:     { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textMuted },
  segOn:      { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textStrong, background: c.surface, boxShadow: '0 1px 2px rgba(15,23,42,0.08)' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:         { ...tbl.th, textAlign: 'left', whiteSpace: 'nowrap' },
  thNarrow:   { ...tbl.th, textAlign: 'left', width: '34px' },
  thNum:      { ...tbl.thR, whiteSpace: 'nowrap' },
  td:         { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  tdRank:     { ...tbl.td, color: c.textMuted, fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
  tdBar:      { ...tbl.td, width: '30%' },
  num:        { ...tbl.tdR, color: c.textStrong },
  barTrack:   { display: 'block', height: '7px', borderRadius: '4px', background: c.surfaceSunk, overflow: 'hidden' },
  barFill:    { display: 'block', height: '100%', borderRadius: '4px' },
  podBadge:   { display: 'inline-flex', alignItems: 'center', gap: '5px', borderRadius: '4px', padding: '1px 6px', fontSize: '10.5px', fontWeight: 600 },
  podDot:     { width: '6px', height: '6px', borderRadius: '2px', flexShrink: 0 },
  flag:       { color: c.warningText, fontWeight: 700 },
  noRows:     { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '20px' },
  empty:      { border: `1px dashed ${c.borderStrong}`, borderRadius: '10px', background: c.surface,
    padding: '64px 24px', textAlign: 'center' },
  emptyTitle: { fontSize: '13px', fontWeight: 600, color: c.text },
  emptySub:   { fontSize: '12px', color: c.textMuted, marginTop: '4px' },
  foot:       { fontSize: '11px', color: c.textMuted, margin: '10px 0 0', lineHeight: 1.5 },
  ok:         { fontSize: '12px', color: c.successText, margin: 0 },
  pillBtn:    { ...btn.secondaryXs },
  pillOn:     { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:      { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  chip:       { fontSize: '11px', color: c.textMuted },
  notice:     { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:      { fontSize: '13px', color: c.dangerText },
  muted:      { fontSize: '12px', color: c.textMuted, margin: 0 },
};
